import { AppSettings, MapSettings } from '@fc-core/models/app.settings';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import { catchError, tap } from 'rxjs/operators';
import {
  MomentService,
  setDefaultTimezone,
} from '@fc-core/services/moment.service';
import {
  InsideMarkerLabel,
  MarkersType,
} from '@fc-map/vehicle-markers/models/marker-type';
import { LabelType } from '@fc-map/vehicle-markers/models/label.type';
import { decodeTokenData } from 'src/app/auth/utils/token-utils';

const APP_SETTINGS_CACHE_KEY = 'employeeAppSettings';

interface CachedAppSettings {
  employeeId: string;
  appSettings: AppSettings;
}

const DEFAULT_MAP_SETTINGS: MapSettings = {
  markerShape: MarkersType.ARROW,
  markerOutsideLabelFields: [
    'operator_name',
    'vehicle_groups',
    'vehicle_identifier',
  ],
  markerInsideLabel: InsideMarkerLabel.ICON,
  markerOutsideLabelStyle: LabelType.WHITE,
  markerFollowingZoom: 18,
  markerClusteringEnabled: true,
};

@Injectable({
  providedIn: 'root',
})
export class AppSettingsService {
  private readonly http = inject(HttpClient);
  private readonly momentService = inject(MomentService);

  getEmployeeAppSettings(): Observable<AppSettings | null> {
    return this.http
      .get<AppSettings>(`${environment.apiUrl}api/settings/employee/`)
      .pipe(
        tap((appSettings: AppSettings) => {
          this.applyAppSettings(appSettings);
          this.cacheAppSettings(appSettings);
        }),
        catchError(() => of(null)),
      );
  }

  getCachedEmployeeAppSettings(): AppSettings | null {
    const employeeId = this.getCurrentEmployeeId();
    if (!employeeId) return null;

    const cached = this.readCachedAppSettings();
    if (!cached || cached.employeeId !== employeeId) return null;

    this.applyAppSettings(cached.appSettings);
    return cached.appSettings;
  }

  refreshEmployeeAppSettings(): void {
    this.getEmployeeAppSettings().subscribe();
  }

  clearCachedEmployeeAppSettings(): void {
    localStorage.removeItem(APP_SETTINGS_CACHE_KEY);
  }

  updateEmployeeAppSettings(mapSettings: MapSettings): Observable<any> {
    return this.http.put(`${environment.apiUrl}api/settings/employee/`, {
      settings: { map: mapSettings },
    });
  }

  private applyAppSettings(appSettings: AppSettings): void {
    localStorage.setItem(
      'markersSettings',
      JSON.stringify(appSettings.settings?.map ?? DEFAULT_MAP_SETTINGS),
    );

    if (appSettings.timezone) {
      this.momentService.timezone = appSettings.timezone;
      setDefaultTimezone(appSettings.timezone);
    }
  }

  private cacheAppSettings(appSettings: AppSettings): void {
    const employeeId = this.getCurrentEmployeeId();
    if (!employeeId) return;

    const cached: CachedAppSettings = { employeeId, appSettings };
    localStorage.setItem(APP_SETTINGS_CACHE_KEY, JSON.stringify(cached));
  }

  private readCachedAppSettings(): CachedAppSettings | null {
    const raw = localStorage.getItem(APP_SETTINGS_CACHE_KEY);
    if (!raw) return null;
    try {
      const parsed: CachedAppSettings = JSON.parse(raw);
      return parsed?.appSettings?.settings ? parsed : null;
    } catch {
      return null;
    }
  }

  private getCurrentEmployeeId(): string | null {
    return decodeTokenData(localStorage.getItem('token'))?.employee_id ?? null;
  }
}
